import React, { useEffect, useState, useRef } from "react";
import PropTypes from "prop-types";
import { Box, Typography, IconButton, alpha } from "@mui/material";
import { makeStyles } from "@mui/styles";
import clsx from "clsx";
import CheckBoxOutlinedIcon from "@mui/icons-material/CheckBoxOutlined";
import IndeterminateCheckBoxOutlinedIcon from "@mui/icons-material/IndeterminateCheckBoxOutlined";
import { setInteractionColor } from "../../../../redux/interactionsSlice";
import { useDispatch } from "react-redux";
import { interactionsAPI } from "../../../../api";

// Color configuration
const HIGHLIGHT_COLORS = [
  {
    color: "#BEF3BF",
    text: "colors.green",
    default: "Green",
    hlColor: "#BEF3BF"
  },
  {
    color: "#B9E7FF",
    text: "colors.cyan",
    default: "Cyan",
    hlColor: "#B9E7FF"
  },
  {
    color: "#ECC5FF",
    text: "colors.pink",
    default: "Pink",
    hlColor: "#ECC5FF"
  },
  { color: "#FF7F74", text: "colors.red", default: "Red", hlColor: "#FF7F74" },
  {
    color: "#FFE690",
    text: "colors.yellow",
    default: "Yellow",
    hlColor: "#FFE690"
  }
];

// Singleton for color assignment
const colorAssigner = new (class {
  constructor() {
    this.assignedColors = new Map();
    this.nextColorIndex = 0;
  }

  getColorForQuestion(questionId) {
    if (this.assignedColors.has(questionId)) {
      return HIGHLIGHT_COLORS[this.assignedColors.get(questionId)];
    }
    const colorIndex = this.nextColorIndex++ % HIGHLIGHT_COLORS.length;
    this.assignedColors.set(questionId, colorIndex);
    return HIGHLIGHT_COLORS[colorIndex];
  }
})();

const useStyles = makeStyles((theme) => ({
  questionCard: {
    display: "flex",
    flexDirection: "column",
    margin: `0 ${theme.spacing(2)}`,
    borderRadius: "4px 4px 0px 0px",
    cursor: "pointer",
    boxShadow: "0px -1px 0px 0px rgba(255, 255, 255, 0.12) inset",
    transition: "background-color 0.2s ease-in-out",
    backgroundColor: theme.palette.action.hover,
    position: "relative",
    overflow: "hidden"
  },
  questionCardNotCombined: {
    marginBlock: theme.spacing(2)
  },
  header: {
    display: "flex",
    alignItems: "center",
    padding: theme.spacing(1)
  },
  questionNumber: {
    fontWeight: "bold",
    marginRight: theme.spacing(1)
  },
  questionContent: {
    flexGrow: 1,
    wordBreak: "break-word"
  },
  footer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    color: theme.palette.text.secondary,
    backgroundColor: alpha(theme.palette.blue.tertiary, 0.2),
    paddingInline: theme.spacing(1.5)
  },
  highlights: {
    fontSize: "13px"
  },
  answerIcon: {
    padding: theme.spacing(0.5),
    color: theme.palette.text.disabled
  },
  studentQuestion: {
    borderLeft: `4px solid ${theme.palette.warning.main}`
  },
  selected: {
    backgroundColor: alpha(theme.palette.blue.tertiary, 0.45)
  },
  rippleContainer: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    pointerEvents: "none",
    zIndex: 5,
    overflow: "hidden"
  },
  waveEffect: {
    position: "absolute",
    height: "100%",
    width: "50%",
    pointerEvents: "none",
    left: "-50%",
    top: 0,
    animation: "$waveSlide 0.8s cubic-bezier(0.4, 0, 0.2, 1) forwards",
    background: `linear-gradient(90deg, ${alpha(theme.palette.primary.light, 0)} 0%, ${alpha(theme.palette.primary.light, 0.25)} 50%, ${alpha(theme.palette.primary.light, 0)} 100%)`,
    zIndex: 6
  },
  "@keyframes waveSlide": {
    "0%": { left: "-50%", transform: "scaleY(0.7)" },
    "50%": { transform: "scaleY(1)" },
    "100%": { left: "100%", transform: "scaleY(0.7)" }
  }
}));

const QuestionCard = ({
  question,
  index,
  isSelected = false,
  highlights = [],
  onClick,
  onAnswerClick,
  answer,
  showRippleEffect = false,
  combinedViewMode
}) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [borderColor, setBorderColor] = useState(null);
  const [showRipple, setShowRipple] = useState(false);
  const prevHighlightCount = useRef(0);

  // Derived values
  const { id, content, user_type } = question;
  const highlightsCount = highlights.filter(
    (h) => h.interaction_id === id
  ).length;
  const isTeacherQuestion = user_type === "TEACHER";
  const hasAnswer = Boolean(answer?.content);

  // Handle color assignment and ripple effect
  useEffect(() => {
    // Assign color for teacher questions
    if (isTeacherQuestion && !borderColor) {
      const colorData = colorAssigner.getColorForQuestion(id);
      setBorderColor(colorData.color);

      // Update Redux and API
      dispatch(setInteractionColor(id, colorData.color));
      interactionsAPI.updateHighlightsColorByQuestionId(
        id,
        id, // Fallback to question.id if internalId is not available
        colorData.color
      );
    }

    // Handle ripple effect
    if (showRippleEffect || highlightsCount > prevHighlightCount.current) {
      setShowRipple(true);
      const timer = setTimeout(() => setShowRipple(false), 850);
      prevHighlightCount.current = highlightsCount;
      return () => clearTimeout(timer);
    }

    prevHighlightCount.current = highlightsCount;
  }, [
    id,
    isTeacherQuestion,
    dispatch,
    borderColor,
    showRippleEffect,
    highlightsCount
  ]);

  return (
    <Box
      className={clsx(
        classes.questionCard,
        !combinedViewMode && classes.questionCardNotCombined
      )}
      onClick={() => onClick(question)}>
      {/* Ripple animation */}
      {showRipple && (
        <Box className={classes.rippleContainer}>
          <Box className={classes.waveEffect} />
        </Box>
      )}

      {/* Question header */}
      <Box
        className={clsx(
          classes.header,
          !isTeacherQuestion && classes.studentQuestion,
          isSelected && classes.selected
        )}
        style={
          isTeacherQuestion
            ? { borderLeft: `4px solid ${borderColor || "#9c27b0"}` }
            : {}
        }>
        <Typography variant="body1" className={classes.questionNumber}>
          {index + 1}
        </Typography>
        <Typography variant="body1" className={classes.questionContent}>
          {content}
        </Typography>
      </Box>

      {/* Footer (only shown when selected and not in combined mode) */}
      {isSelected && !combinedViewMode && (
        <Box className={classes.footer}>
          <Typography variant="caption" className={classes.highlights}>
            Highlights: {highlightsCount}
          </Typography>
          <IconButton
            size="small"
            className={classes.answerIcon}
            onClick={(e) => {
              e.stopPropagation();
              onAnswerClick?.(question);
            }}
            aria-label={hasAnswer ? "View answer" : "Add answer"}>
            <Typography variant="caption" className={classes.highlights}>
              Answer
            </Typography>
            {hasAnswer ? (
              <CheckBoxOutlinedIcon className={classes.answerIcon} />
            ) : (
              <IndeterminateCheckBoxOutlinedIcon
                className={classes.answerIcon}
              />
            )}
          </IconButton>
        </Box>
      )}
    </Box>
  );
};

QuestionCard.propTypes = {
  question: PropTypes.shape({
    id: PropTypes.string.isRequired,
    content: PropTypes.string.isRequired,
    user_type: PropTypes.string.isRequired,
    internalId: PropTypes.string
  }).isRequired,
  index: PropTypes.number.isRequired,
  isSelected: PropTypes.bool,
  highlights: PropTypes.array,
  onClick: PropTypes.func.isRequired,
  onAnswerClick: PropTypes.func,
  answer: PropTypes.object,
  showRippleEffect: PropTypes.bool,
  combinedViewMode: PropTypes.bool
};

export default QuestionCard;
